import { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "./common/Card";
import { Input } from "./common/Input";
import Button from "./common/Button";
import Spinner from "./common/Spinner";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import {
  Search,
  User,
  Mail,
  FileText,
  ArrowLeft,
  Loader2,
  Edit,
  Plus,
  Eye,
  Info,
  X,
  ScanLine,
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogClose, DialogTitle } from "./common/Dialog";
import ClinicalHistoryView from "./ClinicalHistoryView";
import PatientSummaryInfoDialog from "./PatientSummaryInfoDialog";
import PatientHealthHistoryDialog from "./PatientHealthHistoryDialog";
import { formatDateDMY } from "../lib/ScheduleUtils";
import useBrandTranslation from "../hooks/useBrandTranslation";

function calcAge(dateStr) {
  if (!dateStr) return null;
  const birth = new Date(dateStr);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const GENDER_LABELS = { male: "Masculino", female: "Femenino", other: "Otro", prefer_not: "Prefiere no decir" };
const YES_NO = { yes: "Sí", no: "No" };

function PatientInfoDialog({ patient }) {
  const { t } = useBrandTranslation();
  if (!patient) return null;

  const age = calcAge(patient.birth_date);
  const bloodFull = [patient.blood_type, patient.blood_factor].filter(Boolean).join("") || null;

  const sections = [
    {
      title: "Datos Personales",
      items: [
        { label: "DNI", value: patient.dni },
        { label: "Género", value: GENDER_LABELS[patient.gender] },
        { label: "Fecha de Nacimiento", value: patient.birth_date ? formatDateDMY(patient.birth_date) : null },
        { label: "Edad", value: age != null ? `${age} años` : null },
      ],
    },
    {
      title: "Obra Social",
      items: [
        { label: "Obra Social / Prepaga", value: patient.health_insurance_name },
        { label: "Nº Afiliado", value: patient.health_insurance_number },
        { label: "Plan", value: patient.health_insurance_plan },
      ],
    },
    {
      title: "Datos de Salud",
      items: [
        { label: "Grupo Sanguíneo", value: bloodFull },
        { label: "Tabaquismo", value: YES_NO[patient.smoking] },
        { label: "Sedentarismo", value: YES_NO[patient.sedentary] },
        { label: "Tóxicos", value: YES_NO[patient.toxics] },
      ],
    },
    {
      title: "Antecedentes Médicos",
      items: [
        { label: "", value: patient.medical_antecedents, full: true },
      ],
    },
    {
      title: "Discapacidad",
      items: [
        { label: "Tipo", value: patient.disability && patient.disability !== "none" ? patient.disability : null },
        { label: "Detalle", value: patient.disability_detail },
      ],
    },
    {
      title: "Contacto de Emergencia",
      items: [
        { label: "Nombre", value: [patient.emergency_contact_name, patient.emergency_contact_last_name].filter(Boolean).join(" ") || null },
        { label: "Teléfono", value: patient.emergency_contact_phone },
      ],
    },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full w-7 h-7 text-blue-600 hover:bg-blue-100 transition-colors"
          title={t("app.clinical.viewPatientBackground", "Ver antecedentes del paciente")}
        >
          <Info className="w-5 h-5" />
        </button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-lg max-h-[85vh] overflow-y-auto rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <DialogTitle className="text-lg font-bold text-gray-900">
            {t("app.clinical.patientBackgroundTitle", "Antecedentes del Paciente")}
          </DialogTitle>
          <DialogClose asChild>
            <button type="button" className="rounded-full p-1 hover:bg-gray-100 transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </DialogClose>
        </div>
        <div className="space-y-4">
          {sections.map((section) => {
            const hasData = section.items.some((i) => i.value);
            if (!hasData) return null;
            return (
              <div key={section.title}>
                <h4 className="text-sm font-semibold text-gray-700 mb-2 border-b pb-1">
                  {section.title}
                </h4>
                <div className="space-y-1">
                  {section.items.map((item, idx) => {
                    if (!item.value) return null;
                    if (item.full) {
                      return (
                        <p key={idx} className="text-sm text-gray-700 bg-gray-50 rounded p-2 whitespace-pre-wrap">
                          {item.value}
                        </p>
                      );
                    }
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-500 min-w-[120px]">{item.label}:</span>
                        <span className="text-gray-800 font-medium">{item.value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClinicalHistoryManagement() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  const { profiles, loading, clinicalHistories, appointments } = useData();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Verificar permisos
  if (!user || (user.role !== "doctor" && user.role !== "secretary")) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>{t("app.appointments.restrictedAccess", "Acceso Restringido")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600">
              Esta sección es solo para médicos y secretarias.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Pacientes que el profesional ha atendido (solo para médicos: historias clínicas o turnos con este doctor)
  const patientsAttendedByDoctor = useMemo(() => {
    if (!user?.id || user.role !== "doctor") return null;
    const ids = new Set();
    (clinicalHistories || []).forEach((h) => {
      if (h.doctor_id === user.id && h.patient_id) ids.add(h.patient_id);
    });
    (appointments || []).forEach((a) => {
      if (a.doctor_id === user.id && a.patient_id) ids.add(a.patient_id);
    });
    return ids;
  }, [user?.id, user?.role, clinicalHistories, appointments]);

  // Filtrar pacientes: si es médico, solo los que ha atendido; si es secretaria, todos los pacientes
  const patients = useMemo(() => {
    const allPatients = profiles.filter((profile) => profile.role === "patient");
    if (user?.role === "doctor" && patientsAttendedByDoctor) {
      return allPatients.filter((p) => patientsAttendedByDoctor.has(p.id));
    }
    return allPatients;
  }, [profiles, user?.role, patientsAttendedByDoctor]);

  // Buscar pacientes por email, DNI, nombre o apellido (dentro de la lista ya filtrada)
  const searchResults = useMemo(() => {
    if (!searchTerm.trim()) return [];

    const term = searchTerm.toLowerCase().trim();

    return patients.filter((patient) => {
      const name = (patient.name || "").toLowerCase();
      const lastName = (patient.last_name || "").toLowerCase();
      const email = (patient.email || "").toLowerCase();
      const dni = (patient.dni || patient.document_number || "").toString().toLowerCase();
      const fullName = `${name} ${lastName}`.toLowerCase();

      return (
        name.includes(term) ||
        lastName.includes(term) ||
        email.includes(term) ||
        dni.includes(term) ||
        fullName.includes(term)
      );
    });
  }, [searchTerm, patients]);

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm("");
  };

  const handleViewHistory = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm("");
  };

  const handleAddHistory = (patient) => {
    const today = new Date().toISOString().split("T")[0];
    navigate(
      `/doctor/clinical-history/add?patientId=${patient.id}&doctorId=${user.id}&consultationDate=${today}`
    );
  };

  const handleDigitizeHistory = (patient) => {
    const today = new Date().toISOString().split("T")[0];
    navigate(
      `/doctor/clinical-history/add?mode=digitize&patientId=${patient.id}&doctorId=${user.id}&consultationDate=${today}`
    );
  };

  const handleBack = () => {
    if (selectedPatient) {
      setSelectedPatient(null);
    } else {
      utils.goBack();
    }
  };

  // Si hay un paciente seleccionado, mostrar su historia clínica
  if (selectedPatient) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <Button
              variant="outline"
              onClick={handleBack}
              className="mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t("app.clinical.backToSearch", "Volver a Búsqueda")}
            </Button>
            {/* Mobile: datos arriba y botones debajo (con wrap); desktop: en fila */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {t("app.clinical.historyTitle", "Historia Clínica")}
                </h1>
                <div className="flex items-center gap-2 text-lg text-gray-700">
                  <User className="w-5 h-5 shrink-0" />
                  <span className="font-medium break-words min-w-0">
                    {selectedPatient.name} {selectedPatient.last_name}
                  </span>
                  <PatientInfoDialog patient={selectedPatient} />
                </div>
                <p className="text-sm text-gray-600 break-all">{selectedPatient.email}</p>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <PatientHealthHistoryDialog
                  patientId={selectedPatient.id}
                  patientName={`${selectedPatient.name} ${selectedPatient.last_name}`}
                  triggerSize="default"
                />
                {user.role === "doctor" && (
                  <>
                    <Button
                      onClick={() => handleAddHistory(selectedPatient)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t("app.commonUi.add", "Agregar")}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDigitizeHistory(selectedPatient)}
                      className="flex items-center gap-2"
                    >
                      <ScanLine className="w-4 h-4" />
                      {t("app.clinical.digitizeButton", "Digitalizar Historias")}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <ClinicalHistoryView
            patientId={selectedPatient.id}
            showAddButton={user.role === "doctor"}
            child={true}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => utils.goBack()}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />{t("common.backHome", "Volver al Inicio")}</Button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Historias Clínicas
          </h1>
          <p className="text-gray-600">
            {user.role === "doctor"
              ? "Solo puedes buscar pacientes que hayas atendido (con turno o historia clínica). Busca por email, DNI, nombre o apellido."
              : "Busca pacientes por email, DNI, nombre o apellido para ver y gestionar sus historias clínicas"}
          </p>
        </div>

        {/* Búsqueda */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Buscar Paciente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder={t("app.clinical.searchPatient", "Buscar por email, DNI, nombre o apellido...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {searchResults.length} resultado{searchResults.length !== 1 ? "s" : ""} encontrado{searchResults.length !== 1 ? "s" : ""}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Resultados de búsqueda */}
        {searchTerm && (
          <Card className="mb-20">
            <CardHeader>
              <CardTitle>{t("app.clinical.searchResults", "Resultados de Búsqueda")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-600">{t("app.commonUi.searching", "Buscando...")}</span>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="text-center py-12">
                  <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">
                    No se encontraron pacientes
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    Intenta con otros términos de búsqueda
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {searchResults.map((patient) => (
                    <div
                      key={patient.id}
                      className="border rounded-lg p-4 transition-all hover:shadow-md cursor-pointer"
                      onClick={() => handleSelectPatient(patient)}
                    >
                      <div className="flex gap-3">
                        <div className="w-12 h-12 shrink-0 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-lg text-gray-900">
                            {patient.name} {patient.last_name}
                          </p>
                          <div className="mt-1 flex flex-col gap-1 text-sm text-gray-600">
                            {patient.email && (
                              <p className="flex items-start gap-1.5 break-words">
                                <Mail className="mt-0.5 h-3 w-3 shrink-0" />
                                <span className="min-w-0 break-all">{patient.email}</span>
                              </p>
                            )}
                            {(patient.dni || patient.document_number) && (
                              <p className="flex items-start gap-1.5 break-words">
                                <FileText className="mt-0.5 h-3 w-3 shrink-0" />
                                <span className="min-w-0">
                                  DNI: {patient.dni || patient.document_number}
                                </span>
                              </p>
                            )}
                          </div>
                          <div
                            className="mt-3 flex flex-wrap gap-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button
                              variant="outline"
                              size="xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewHistory(patient);
                              }}
                              className="flex items-center gap-2 p-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span>{t("app.clinical.viewHistory", "Ver Historia")}</span>
                            </Button>
                            {user.role === "doctor" && (
                              <>
                                <Button
                                  variant="default"
                                  size="xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAddHistory(patient);
                                  }}
                                  className="flex items-center gap-2 p-1 pr-2"
                                >
                                  <Plus className="w-4 h-4" />
                                  <span>{t("app.commonUi.add", "Agregar")}</span>
                                </Button>
                                <Button
                                  variant="outline"
                                  size="xs"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDigitizeHistory(patient);
                                  }}
                                  className="flex items-center gap-2 p-1 pr-2"
                                >
                                  <ScanLine className="w-4 h-4" />
                                  <span>{t("app.clinical.digitizeButton", "Digitalizar Historias")}</span>
                                </Button>
                              </>
                            )}
                            <PatientSummaryInfoDialog patient={patient} />
                            <PatientHealthHistoryDialog
                              patientId={patient.id}
                              patientName={`${patient.name} ${patient.last_name}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Mensaje inicial */}
        {!searchTerm && (
          <Card>
            <CardContent className="text-center py-12">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4 mt-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Buscar Paciente
              </h3>
              <p className="text-gray-500">
                Ingresa el email, DNI, nombre o apellido del paciente para comenzar
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

